import { AddCircle, CheckCircleOutline, ChevronRight } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogTitle, Tooltip } from '@mui/material';
import {
    GridActionsCellItem,
    GridActionsCellItemProps,
    GridRowParams,
    GridValueFormatterParams,
} from '@mui/x-data-grid-premium';
import { selectedProductsContext } from 'pages/managePricing/managePricing';
import { useMemo, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { GridColumns } from 'shared/types';
import { ROUTES } from 'routing';
import { PERMISSIONS } from 'shared/constants';
import { useUserPermissions } from 'shared/hooks';
import { useApproverUsers } from 'shared/hooks/useApproverUsers';
import { useCreateProductRequest } from 'shared/hooks/useCreateProductRequest';
import UseDynamicUrl from 'shared/hooks/useDynamicUrl';
import { useSelectedItems } from 'shared/providers/provide-selected-items';
import { moneyFourDecimalFormatter } from 'shared/utility/dataGrid';
import { TAddProductsData } from 'types/backend';
import { CatalogProductsResolver } from './CatalogProductsResolver';
import { ExclusiveProductsResolver } from './ExclusiveProductsResolver';
import { PrivateProductsResolver } from './PrivateProductsResolver';
import { SelectedProductsTable } from './selectedProductsTable';

interface AddProductsProps {
    priceTier: string;
}

export function AddProducts({ priceTier }: AddProductsProps) {
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const history = useHistory();
    const { path: managingPricesPath } = UseDynamicUrl({ route: ROUTES.managePricing });

    const { selectedItems, updateItems, clearItems } = useSelectedItems(selectedProductsContext);

    const { isLoading: approverUsersIsLoading, isFetching: approverUsersIsFetching } = useApproverUsers();
    const isApproverUsersLoading = approverUsersIsLoading || approverUsersIsFetching;

    const { onSubmit, isLoading } = useCreateProductRequest();

    const { permissions } = useUserPermissions();
    const isAdmin = useMemo<boolean>(() => permissions.some((p) => p === PERMISSIONS.admin), [permissions]);

    const canCreateProduct =
        isAdmin || !selectedItems.some((selectedItem) => selectedItem.price < selectedItem.targetprice);

    const handleClick = (params: TAddProductsData) => () => {
        // product price is the same as targetprice as default
        updateItems([...selectedItems, { ...params, price: params.targetprice }]);
    };

    const determineIcon: (
        params: GridRowParams<TAddProductsData>
    ) => React.ReactElement<GridActionsCellItemProps, string | React.JSXElementConstructor<any>>[] = (params) => {
        return selectedItems.map((selectedItem) => selectedItem.id).includes(params.row.id)
            ? [<GridActionsCellItem icon={<CheckCircleOutline />} label='Selected' />]
            : [<GridActionsCellItem icon={<AddCircle />} label='Select' onClick={handleClick(params.row)} />];
    };

    const columnConfig: GridColumns<TAddProductsData> = [
        {
            headerName: 'Select Products',
            field: 'actions',
            type: 'actions',
            hide: false,
            filterable: false,
            pinnable: false,
            width: 200,
            getActions: determineIcon,
        },
        {
            headerName: 'Product',
            field: 'itemid',
            hideable: false,
            pinnable: false,
            valueFormatter: (params: GridValueFormatterParams<number>) => `${params.value}`,
            width: 200,
        },
        {
            headerName: 'Region',
            field: 'region',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Group',
            field: 'productcategoryname',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Type',
            field: 'filmtype',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'Target Price',
            field: 'targetprice',
            hideable: false,
            pinnable: false,
            valueFormatter: moneyFourDecimalFormatter,
            type: 'number',
            filterable: false,
            width: 200,
        },
    ];

    const handleSubmit = async () => {
        await onSubmit(selectedItems);
        clearItems();
        setIsDialogOpen(false);
        history.push(managingPricesPath);
    };

    return (
        <Switch>
            <Route path={ROUTES.addProducts}>
                <Switch>
                    <Route path={ROUTES.addProductsCatalog}>
                        <CatalogProductsResolver columnConfig={columnConfig} />
                    </Route>

                    <Route path={ROUTES.addProductsExclusive}>
                        <ExclusiveProductsResolver columnConfig={columnConfig} />
                    </Route>

                    <Route path={ROUTES.addProductsPrivate}>
                        <PrivateProductsResolver columnConfig={columnConfig} />
                    </Route>
                </Switch>

                {!isApproverUsersLoading && <SelectedProductsTable />}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title={canCreateProduct ? '' : 'Market Price cannot be less than Target Price'}>
                        <span>
                            <Button
                                variant={'contained'}
                                sx={{
                                    backgroundColor: 'secondary.main',
                                    color: 'black',
                                    '&:hover': {
                                        backgroundColor: 'secondary.light',
                                        color: 'black',
                                    },
                                }}
                                onClick={() => setIsDialogOpen(true)}
                                endIcon={<ChevronRight />}
                                disabled={selectedItems.length === 0 || !canCreateProduct}
                            >
                                Create Products
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                <Dialog open={isDialogOpen} aria-labelledby='responsive-dialog-title'>
                    <DialogTitle id='responsive-dialog-title'>
                        Are you sure you want to create the products?
                    </DialogTitle>
                    <DialogActions>
                        <Button autoFocus onClick={() => setIsDialogOpen(false)} disabled={isLoading}>
                            No
                        </Button>
                        <Tooltip title={canCreateProduct ? '' : 'Market Price cannot be less than Target Price'}>
                            <span>
                                <Button
                                    autoFocus
                                    onClick={handleSubmit}
                                    disabled={isLoading || selectedItems.length === 0 || !canCreateProduct}
                                >
                                    Yes
                                </Button>
                            </span>
                        </Tooltip>
                    </DialogActions>
                </Dialog>
            </Route>
        </Switch>
    );
}
